"use client";

import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCurrencySymbol, PriceCurrency } from "@/features/calendar/types/pricing";

interface AmountStepProps {
  control: Control<any>;
}

export function AmountStep({ control }: AmountStepProps) {
  const { t } = useTranslation(["finances", "common", "calendar"]);
  const watchedCurrency = useWatch({ control, name: "currency" });

  // price tier quick-fill removed for now (not used)

  return (
    <div className="flex h-full flex-col justify-center px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:currency-dollar"
            width="1.75rem"
          />
          {t("finances:createPayment.amount")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.amountHelp")}
        </p>
      </div>

      {/* Amount Input */}
      <div className="flex justify-center">
        <div className="w-full max-w-2xl space-y-6">
          <div className="space-y-1.5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
              <label
                className="text-accent-foreground text-sm font-medium sm:text-base"
                htmlFor="payment-amount"
              >
                {t("finances:createPayment.amount")}
              </label>
              <FormField
                control={control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <div className="bg-muted/30 hover:bg-muted/50 flex h-8 items-center self-start rounded-full px-3 sm:h-9 sm:self-auto sm:px-4">
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-8 min-w-[70px] border-0 bg-transparent p-0 px-1 text-sm shadow-none focus:ring-0 focus:ring-offset-0 sm:h-9 sm:min-w-[85px] sm:px-2">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent align="center" className="z-50">
                          <SelectItem value={PriceCurrency.Eur}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">€</span>
                              <span className="text-sm">EUR</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Gbp}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">£</span>
                              <span className="text-sm">GBP</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Pln}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">zł</span>
                              <span className="text-sm">PLN</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Usd}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">$</span>
                              <span className="text-sm">USD</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <div className="bg-muted/30 group relative rounded-xl">
                    <div className="absolute top-1/2 left-0 flex h-14 w-14 -translate-y-1/2 items-center justify-center bg-transparent sm:w-18">
                      <span className="text-accent-foreground text-xl font-bold sm:text-2xl">
                        {getCurrencySymbol((watchedCurrency as PriceCurrency) ?? PriceCurrency.Usd)}
                      </span>
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        className="placeholder:text-muted-foreground/50 h-14 border-0 bg-transparent pr-4 pl-16 text-xl font-semibold focus:ring-0 focus:ring-offset-0 sm:h-18 sm:pl-20 sm:text-2xl"
                        id="payment-amount"
                        placeholder="0.00"
                        type="text"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
